/* eslint-disable no-unused-vars */
import { useFormik } from 'formik';
import { useState } from 'react';
import * as yup from 'yup';

import { Button, Input, SideModal, Typography } from 'design_system/src';
import { ReactComponent as AddCircle } from 'design_system/src/static/icons/add-circle.svg';
import { ReactComponent as Trash } from 'design_system/src/static/icons/delete.svg';
import { ReactComponent as Save } from 'design_system/src/static/icons/save.svg';

import { createBrand } from 'models/inventory/controllers/brand.controller';
import { toasterOptions } from 'models/shared/enums/toaster.enum';
import { toasterNotification } from 'models/shared/helpers/toasterNotification';

interface ModelRow {
	name: string;
	cylinderCapacity: string;
}

interface FormValues {
	name: string;
	abbreviation: string;
	models: ModelRow[];
}

const validationSchema = yup.object({
	name: yup.string().required('Nombre de marca requerido'),
	abbreviation: yup
		.string()
		.required('Abreviación requerida')
		.min(2, 'Mínimo 2 caracteres')
		.max(5, 'Máximo 5 caracteres'),
	models: yup.array().of(
		yup.object({
			name: yup.string().required('Nombre del modelo requerido'),
			cylinderCapacity: yup
				.string()
				.required('Cilindrada requerida')
				.matches(/^[\d,\s]+$/, 'Ingresa números separados por coma'),
		}),
	),
});

const defaults: FormValues = {
	name: '',
	abbreviation: '',
	models: [{ name: '', cylinderCapacity: '' }],
};

interface CreateBrandModalProps {
	open: boolean;
	setOpen: (open: boolean) => void;
	onCreated?: () => void;
}

function CreateBrandModal({ open, setOpen, onCreated }: CreateBrandModalProps) {
	const [loading, setLoading] = useState(false);
	const [showErrors, setShowErrors] = useState(false);

	const form = useFormik<FormValues>({
		initialValues: defaults,
		validationSchema,
		onSubmit: async (vals, helpers) => {
			setLoading(true);
			try {
				const dto = {
					name: vals.name.toUpperCase().trim(),
					abbreviation: vals.abbreviation.toUpperCase().trim(),
					models: vals.models
						.filter((m) => m.name.trim())
						.map((m) => ({
							name: m.name.toUpperCase().trim(),
							cylinderCapacity: m.cylinderCapacity
								.split(',')
								.map((c) => parseInt(c.trim(), 10))
								.filter((n) => !Number.isNaN(n)),
						})),
				};
				await createBrand(dto);
				toasterNotification({
					msg: `Marca ${dto.name} creada correctamente`,
					toasterType: toasterOptions.success,
					style: 'light',
				});
				helpers.resetForm();
				setOpen(false);
				onCreated?.();
			} catch (err: any) {
				toasterNotification({
					msg:
						typeof err?.message === 'string'
							? err.message
							: 'Error al crear la marca',
					toasterType: toasterOptions.error,
					style: 'light',
				});
			} finally {
				setLoading(false);
			}
		},
	});

	const addModel = () => {
		form.setFieldValue('models', [
			...form.values.models,
			{ name: '', cylinderCapacity: '' },
		]);
	};

	const removeModel = (index: number) => {
		const updated = form.values.models.filter((_, i) => i !== index);
		form.setFieldValue(
			'models',
			updated.length ? updated : [{ name: '', cylinderCapacity: '' }],
		);
	};

	const handleSave = () => {
		if (!form.isValid) {
			setShowErrors(true);
			toasterNotification({
				msg: 'Completa todos los campos requeridos',
				toasterType: toasterOptions.error,
				style: 'light',
			});
		}
		form.handleSubmit();
	};

	return (
		<SideModal
			open={open}
			fullOpen={open}
			setOpen={setOpen}
			setFullOpen={setOpen}
			size="small"
		>
			<div className="flex_content_col dim_100_per p_x_md p_y_md overflow_y_auto">
				<div className="display_flex flex_col flex_gap_md flex_grow_1">
					<Typography
						scale="heading4"
						weight="600"
						textColor="neutral_900"
						className="m_b_sm"
					>
						Crear marca
					</Typography>

					<Input
						title="Nombre de la marca"
						name="name"
						type="text"
						placeholder="Ej: BENELLI"
						value={form.values.name}
						onChange={form.handleChange}
						onBlur={form.handleBlur}
						error={showErrors && !!form.errors.name}
						subtitle={showErrors ? form.errors.name : undefined}
					/>

					<Input
						title="Abreviación (2-5 caracteres)"
						name="abbreviation"
						type="text"
						placeholder="Ej: BEN"
						value={form.values.abbreviation}
						onChange={form.handleChange}
						onBlur={form.handleBlur}
						error={showErrors && !!form.errors.abbreviation}
						subtitle={
							showErrors
								? form.errors.abbreviation
								: 'Se usa en el SKU. Ej: BEN → BEN03-2026-01-01-150'
						}
					/>

					<div
						style={{
							background: '#F0F4FF',
							border: '1px solid #C7D6FA',
							borderRadius: '8px',
							padding: '12px 16px',
						}}
					>
						<Typography scale="xsmall" weight="400" textColor="neutral_600">
							Vista previa del SKU con esta abreviación:
						</Typography>
						<Typography scale="medium" weight="600" textColor="primary_300">
							{form.values.abbreviation
								? `${form.values.abbreviation.toUpperCase()}XX-YYYY-XX-XX-CC`
								: 'ABV​XX-YYYY-XX-XX-CC'}
						</Typography>
					</div>

					<div className="display_flex flex_align_center flex_justify_between m_t_sm">
						<Typography scale="heading4" weight="600" textColor="neutral_900">
							Modelos
						</Typography>
						<Button
							variant="ghost"
							scale="small"
							icon={<AddCircle />}
							onClick={addModel}
						>
							Añadir modelo
						</Button>
					</div>

					{form.values.models.map((model, index) => (
						// eslint-disable-next-line react/no-array-index-key
						<div
							key={index}
							style={{
								border: '1px solid #E5E7EB',
								borderRadius: '8px',
								padding: '12px',
								display: 'flex',
								flexDirection: 'column',
								gap: '8px',
							}}
						>
							<div className="display_flex flex_align_center flex_justify_between">
								<Typography scale="small" weight="600" textColor="neutral_700">
									Modelo {index + 1}
								</Typography>
								{form.values.models.length > 1 && (
									<button
										type="button"
										onClick={() => removeModel(index)}
										style={{
											background: 'none',
											border: 'none',
											cursor: 'pointer',
											color: '#EF4444',
											display: 'flex',
											alignItems: 'center',
										}}
									>
										<Trash style={{ width: '16px', height: '16px' }} />
									</button>
								)}
							</div>
							<Input
								title="Nombre del modelo"
								name={`models[${index}].name`}
								type="text"
								placeholder="Ej: TNT"
								value={model.name}
								onChange={form.handleChange}
								onBlur={form.handleBlur}
								error={
									showErrors && !!(form.errors.models?.[index] as any)?.name
								}
								subtitle={
									showErrors
										? (form.errors.models?.[index] as any)?.name
										: undefined
								}
							/>
							<Input
								title="Cilindradas disponibles (separadas por coma)"
								name={`models[${index}].cylinderCapacity`}
								type="text"
								placeholder="Ej: 150, 250, 300"
								value={model.cylinderCapacity}
								onChange={form.handleChange}
								onBlur={form.handleBlur}
								error={
									showErrors &&
									!!(form.errors.models?.[index] as any)?.cylinderCapacity
								}
								subtitle={
									showErrors
										? (form.errors.models?.[index] as any)?.cylinderCapacity
										: undefined
								}
							/>
						</div>
					))}
				</div>

				<div className="display_flex flex_gap_sm m_t_lg">
					<Button
						variant="outline"
						scale="small"
						className="flex_grow_1"
						onClick={() => setOpen(false)}
						disabled={loading}
					>
						Cancelar
					</Button>
					<Button
						scale="small"
						icon={<Save />}
						className="flex_grow_1"
						onClick={handleSave}
						loading={loading}
					>
						Crear marca
					</Button>
				</div>
			</div>
		</SideModal>
	);
}

export default CreateBrandModal;
